import { ResponsiveBar } from "@nivo/bar";
import { format, startOfMonth } from "date-fns";
import { TrendingUp } from "lucide-react";
import { useGetList, useTranslate } from "ra-core";
import { memo, useMemo } from "react";

import type { Load } from "../types";

// Statuses considered "realized" profit (load is picked up, invoiced, or done)
const REALIZED_STATUSES = new Set(["lg_picked_up", "ready_to_invoice", "invoiced", "completed"]);

// Active workflow stages — load is in progress, profit is expected
const ACTIVE_STATUSES = new Set([
  "pending_so_approval",
  "closed_won_pca",
  "closed_won_ppoa",
  "lg_booking",
  "lg_booked",
]);

const threeMonthsAgoDate = new Date();
threeMonthsAgoDate.setMonth(threeMonthsAgoDate.getMonth() - 3);
const THREE_MONTHS_AGO_STR = threeMonthsAgoDate.toISOString().slice(0, 10);

const threeMonthsAheadDate = new Date();
threeMonthsAheadDate.setMonth(threeMonthsAheadDate.getMonth() + 3);
const THREE_MONTHS_AHEAD = threeMonthsAheadDate;

const DEFAULT_LOCALE = "en-US";

export const LoadsChart = memo(() => {
  const translate = useTranslate();
  const acceptedLanguages = navigator
    ? navigator.languages || [navigator.language]
    : [DEFAULT_LOCALE];

  const { data, isPending } = useGetList<Load>("loads", {
    // Fetch enough records for the window. @lte is not supported by the data
    // provider, so we apply the upper-bound cutoff in the useMemo below.
    pagination: { perPage: 2000, page: 1 },
    sort: { field: "pickup_date", order: "ASC" },
    filter: {
      "pickup_date@gte": THREE_MONTHS_AGO_STR,
      "deleted@eq": false,
    },
  });

  const months = useMemo(() => {
    if (!data) return [];

    const byMonth = data.reduce(
      (acc, load) => {
        if (!load.pickup_date) return acc;
        const d = new Date(load.pickup_date);
        // Client-side upper-bound: drop loads beyond 3 months ahead
        if (d > THREE_MONTHS_AHEAD) return acc;
        const month = startOfMonth(d).toISOString();
        if (!acc[month]) acc[month] = [];
        acc[month].push(load);
        return acc;
      },
      {} as Record<string, Load[]>,
    );

    return Object.keys(byMonth)
      .sort()
      .map((month) => {
        const loads = byMonth[month];
        return {
          date: format(new Date(month), "MMM yy"),
          realized: loads
            .filter((l) => REALIZED_STATUSES.has(l.status))
            .reduce((sum, l) => sum + (l.profit_usd ?? 0), 0),
          pending: loads
            .filter((l) => ACTIVE_STATUSES.has(l.status))
            .reduce((sum, l) => sum + (l.profit_usd ?? 0), 0),
        };
      });
  }, [data]);

  if (isPending) return null;

  const range = months.reduce(
    (acc, month) => {
      acc.min = Math.min(acc.min, month.realized, month.pending);
      acc.max = Math.max(acc.max, month.realized + month.pending);
      return acc;
    },
    { min: 0, max: 0 },
  );

  return (
    <div className="flex flex-col">
      <div className="flex items-center mb-4">
        <div className="mr-3 flex">
          <TrendingUp className="text-muted-foreground w-6 h-6" />
        </div>
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("crm.dashboard.loads_chart")}
        </h2>
      </div>
      <div className="h-[400px]">
        <ResponsiveBar
          data={months}
          indexBy="date"
          keys={["realized", "pending"]}
          colors={["#61cdbb", "#97e3d5"]}
          margin={{ top: 30, right: 50, bottom: 30, left: 0 }}
          padding={0.3}
          valueScale={{
            type: "linear",
            min: range.min < 0 ? range.min * 1.2 : 0,
            max: range.max * 1.2 || 1,
          }}
          indexScale={{ type: "band", round: true }}
          enableGridX={true}
          enableGridY={false}
          enableLabel={false}
          tooltip={({ id, value, indexValue }) => (
            <div className="p-2 bg-secondary rounded shadow inline-flex items-center gap-1 text-secondary-foreground">
              <strong>{indexValue}</strong>&nbsp;
              {id === "realized" ? "Realized" : "Pending"}:&nbsp;
              {value.toLocaleString(
                acceptedLanguages.at(0) ?? DEFAULT_LOCALE,
                { style: "currency", currency: "USD" },
              )}
            </div>
          )}
          axisTop={{
            tickSize: 0,
            tickPadding: 12,
            style: {
              ticks: { text: { fill: "var(--color-muted-foreground)" } },
              legend: { text: { fill: "var(--color-muted-foreground)" } },
            },
          }}
          axisBottom={{
            legendPosition: "middle",
            legendOffset: 50,
            tickSize: 0,
            tickPadding: 12,
            style: {
              ticks: { text: { fill: "var(--color-muted-foreground)" } },
              legend: { text: { fill: "var(--color-muted-foreground)" } },
            },
          }}
          axisLeft={null}
          axisRight={{
            format: (v: any) => `$${Math.abs(v / 1000)}k`,
            tickValues: 8,
            style: {
              ticks: { text: { fill: "var(--color-muted-foreground)" } },
              legend: { text: { fill: "var(--color-muted-foreground)" } },
            },
          }}
          markers={
            [
              {
                axis: "y",
                value: 0,
                lineStyle: { strokeOpacity: 0 },
                textStyle: { fill: "#2ebca6" },
                legend: "Realized",
                legendPosition: "top-left",
                legendOrientation: "vertical",
              },
              {
                axis: "y",
                value: 0,
                lineStyle: { strokeOpacity: 0 },
                textStyle: { fill: "#97e3d5" },
                legend: "Pending",
                legendPosition: "top-right",
                legendOrientation: "vertical",
              },
            ] as any
          }
        />
      </div>
    </div>
  );
});
